import React from "react";
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Text,
  Select as DropDown,
  FormControl,
  FormLabel,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiRefreshCw, FiArrowLeft, FiSave } from "react-icons/fi";
import Select from "react-select";

const ColumnMapper = ({
  defaults, // [{ name, required, kind: 'input' | 'output' }]
  fileColumns,
  mapping,
  setMapping,
  setStep, // go back to Step 0
  onClick, // Save handler
  children, // optional custom footer actions
  campaignType,
  questionKeys,
  currentClientAnswers,
  setCurrentClientAnswers,
}) => {
  /* ------- theme‑aware colors (hooks called at top level) ------- */
  const cardBg = useColorModeValue("white", "gray.800");
  const zebraBg = useColorModeValue("gray.50", "gray.700");
  const headerBg = useColorModeValue("gray.100", "gray.700");
  const rowHoverBg = useColorModeValue("gray.200", "gray.600");
  const borderClr = useColorModeValue("gray.200", "gray.700");

  /* split header list */
  const inputDefs = defaults.filter((d) => d.kind === "input");
  const outputDefs = defaults.filter((d) => d.kind === "output");

  /* ----- helpers ----- */
  const autoMap = () =>
    setMapping(
      Object.fromEntries(
        defaults.map(({ name }) => {
          const match = fileColumns.find(
            (c) => c.toLowerCase() === name.toLowerCase()
          );
          return [name, match || ""];
        })
      )
    );

  const mappedCount = defaults.filter((d) => mapping[d.name]).length;

  const renderRows = (defs) =>
    defs.map(({ name, required }, idx) => (
      <Tr
        key={name}
        bg={idx % 2 === 0 ? zebraBg : "transparent"}
        _hover={{ bg: rowHoverBg }}
      >
        <Td fontWeight="medium">
          {name}
          {required ? (
            <Badge ml={2} colorScheme="red" variant="solid">
              required
            </Badge>
          ) : (
            <Badge ml={2} colorScheme="gray" variant="subtle">
              optional
            </Badge>
          )}
        </Td>
        <Td>
          <Select
            options={fileColumns.map((c) => ({ value: c, label: c }))}
            placeholder="— Ignore —"
            isClearable
            value={
              mapping[name]
                ? { value: mapping[name], label: mapping[name] }
                : null
            }
            onChange={(opt) =>
              setMapping((m) => ({ ...m, [name]: opt?.value || "" }))
            }
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              control: (base) => ({
                ...base,
                minHeight: "30px",
                fontSize: "0.78rem",
                borderRadius: "6px",
              }),
            }}
            menuPortalTarget={document.body}
          />
        </Td>
      </Tr>
    ));

  /* ---------------------------- UI ---------------------------- */
  return (
    <VStack spacing={6} align="stretch">
      {/* top bar */}
      <HStack justify="space-between">
        <Badge colorScheme="green" fontSize="0.75rem">
          {mappedCount}/{defaults.length} mapped
        </Badge>
        <Tooltip label="Auto‑match identical names">
          <IconButton
            size="sm"
            icon={<FiRefreshCw />}
            onClick={autoMap}
            aria-label="Auto‑map"
            variant="ghost"
          />
        </Tooltip>
      </HStack>

      {/* ---------- INPUT HEADERS ---------- */}
      <Heading size="sm" color="gray.600" mb={0}>
        Input Columns
      </Heading>
      <Box
        overflow="auto"
        bg={cardBg}
        border="1px solid"
        borderColor={borderClr}
        borderRadius="lg"
        shadow="xs"
        // maxH="225px"
      >
        <Table size="sm">
          <Thead position="sticky" top="0" bg={headerBg} zIndex={1}>
            <Tr>
              <Th w="40%">Default Column</Th>
              <Th w="60%">Map To</Th>
            </Tr>
          </Thead>
          <Tbody>{renderRows(inputDefs)}</Tbody>
        </Table>
      </Box>

      {/* ---------- OUTPUT HEADERS ---------- */}
      <Heading size="sm" color="gray.600" pt={2} mb={0}>
        Output Columns
      </Heading>
      <Box
        overflow="auto"
        bg={cardBg}
        border="1px solid"
        borderColor={borderClr}
        borderRadius="lg"
        shadow="xs"
        // maxH="225px"
      >
        <Table size="sm">
          <Thead position="sticky" top="0" bg={headerBg} zIndex={1}>
            <Tr>
              <Th w="40%">Default Column</Th>
              <Th w="60%">Map To</Th>
            </Tr>
          </Thead>
          <Tbody>{renderRows(outputDefs)}</Tbody>
        </Table>
      </Box>

      {campaignType === "cross_sell" && (
  <Box mt={6} p={4} bg="gray.50" borderRadius="md">
    {[
      {
        key: "current_client_main_contact_full_name",
        prompt: "Current client – main contact’s full name:",
      },
      {
        key: "current_client_main_contact_job_title",
        prompt: "Current client – main contact’s job title:",
      },
      {
        key: "current_client_main_contact_department",
        prompt: "Current client – main contact’s department:",
      },
      {
        key: "current_client_purchased_proposition",
        prompt: "What proposition does the current client already buy from us?",
      },
      {
        key: "current_client_company_name",
        prompt: "Current client company name:",
      },
      {
        key: "current_client_relationship_to_target",
        prompt: "Relationship to target company (Same Company / Parent / Sister):",
      },
      {
        key: "current_client_additional_details",
        prompt: "Any specifics to add to the generated content:",
      },
    ].map(({ key, prompt }) => (
      <FormControl mb={3} key={key}>
        <FormLabel fontSize="sm">{prompt}</FormLabel>

        {key === "current_client_relationship_to_target" ? (
          <DropDown
            size="sm"
            borderRadius="xl"
            value={currentClientAnswers[key] || ""}
            onChange={(e) =>
              setCurrentClientAnswers((prev) => ({
                ...prev,
                [key]: e.target.value,
              }))
            }
          >
            <option value="">Select relationship</option>
            <option value="Same Company">Same Company</option>
            <option value="Parent Company">Parent Company</option>
            <option value="Sister Company">Sister Company</option>
          </DropDown>
        ) : (
          <Input
            size="sm"
            borderRadius="xl"
            value={currentClientAnswers[key] || ""}
            onChange={(e) =>
              setCurrentClientAnswers((prev) => ({
                ...prev,
                [key]: e.target.value,
              }))
            }
            placeholder="Enter answer"
          />
        )}
      </FormControl>
    ))}
  </Box>
)}

      {/* footer actions */}
      <HStack justify="space-between" pt={4}>
        <Button
          leftIcon={<FiArrowLeft />}
          size="sm"
          variant="ghost"
          onClick={() => setStep(0)}
        >
          Back
        </Button>

        {children ?? (
          <Button
            leftIcon={<FiSave />}
            bg="#000"
            _hover={{ bg: "#1a1a1a" }}
            color="#fff"
            size="sm"
            borderRadius="full"
            onClick={onClick}
          >
            Save & Upload
          </Button>
        )}
      </HStack>
    </VStack>
  );
};

export default ColumnMapper;
